import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Form, Table, Spinner } from 'react-bootstrap';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { 
  DocumentArrowDownIcon,
  ChartBarIcon,
  DocumentIcon
} from '@heroicons/react/24/solid';
import { orderAPI } from '../../services/api';
import Header from '../common/Header';
import Footer from '../common/Footer';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend);

interface SummaryRow {
  period: string;
  orders: number;
  spending: number;
  avg_order: number;
}

interface FinancialResponse {
  monthly_labels: string[];
  monthly_spending: number[];
  monthly_order_counts: number[];
  categories: string[];
  category_spending: number[];
  products: string[];
  product_spending: number[];
  summary: SummaryRow[];
  total_spent: number;
  total_orders: number;
  avg_order_value: number;
  monthly_average: number;
}

const FinancialReports: React.FC = () => {
  const [dateRange, setDateRange] = useState('last-6-months');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [data, setData] = useState<FinancialResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const getDateRange = useCallback((): { start?: string; end?: string } => {
    const now = new Date();
    const fmt = (d: Date) => d.toISOString().split('T')[0];
    const end = fmt(now);

    switch (dateRange) {
      case 'last-30-days': {
        const s = new Date(now);
        s.setDate(s.getDate() - 30);
        return { start: fmt(s), end };
      }
      case 'last-90-days': {
        const s = new Date(now);
        s.setDate(s.getDate() - 90);
        return { start: fmt(s), end };
      }
      case 'last-6-months': {
        const s = new Date(now);
        s.setMonth(s.getMonth() - 6);
        return { start: fmt(s), end };
      }
      case 'year-to-date':
        return { start: `${now.getFullYear()}-01-01`, end };
      case 'all-time':
        return {};
      case 'custom':
        return { start: customStart || undefined, end: customEnd || undefined };
      default:
        return {};
    }
  }, [dateRange, customStart, customEnd]);

  const fetchFinancialData = useCallback(async () => {
    setLoading(true);
    try {
      const { start, end } = getDateRange();
      const response = await orderAPI.getFinancialData(start, end);
      setData(response.data as FinancialResponse);
    } catch (error) {
      console.error('Error fetching financial data:', error);
    } finally {
      setLoading(false);
    }
  }, [getDateRange]);

  useEffect(() => {
    if (dateRange === 'custom' && (!customStart || !customEnd)) return;
    fetchFinancialData();
  }, [dateRange, customStart, customEnd, fetchFinancialData]);

  const monthlyData = {
    labels: data?.monthly_labels || [],
    datasets: [
      {
        label: 'Monthly Spending ($)',
        data: data?.monthly_spending || [],
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        tension: 0.4,
        yAxisID: 'y',
      },
      {
        label: 'Order Count',
        data: data?.monthly_order_counts || [],
        borderColor: 'rgb(255, 99, 132)',
        backgroundColor: 'rgba(255, 99, 132, 0.2)',
        tension: 0.4,
        yAxisID: 'y1',
      },
    ],
  };

  const productData = {
    labels: data?.products || [],
    datasets: [
      {
        label: 'Spending by Product ($)',
        data: data?.product_spending || [],
        backgroundColor: [
          'rgba(255, 99, 132, 0.5)',
          'rgba(54, 162, 235, 0.5)',
          'rgba(255, 205, 86, 0.5)',
          'rgba(75, 192, 192, 0.5)',
          'rgba(153, 102, 255, 0.5)',
          'rgba(255, 159, 64, 0.5)',
        ],
        borderColor: [
          'rgba(255, 99, 132, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(255, 205, 86, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(153, 102, 255, 1)',
          'rgba(255, 159, 64, 1)',
        ],
        borderWidth: 1,
      },
    ],
  };

  if (loading) {
    return (
      <div className="d-flex flex-column min-vh-100 app-background">
        <Header />
        <div className="flex-grow-1 d-flex justify-content-center align-items-center">
          <Spinner animation="border" variant="primary" />
        </div>
        <Footer />
      </div>
    );
  }

  const exportToExcel = () => {
    if (!data) return;

    let csv = 'Financial Summary Report\n\n';

    // Summary section
    csv += 'Period,Orders,Total Spending,Average Order Value\n';
    data.summary?.forEach((row) => {
      csv += `${row.period},${row.orders},$${row.spending.toFixed(2)},$${row.avg_order.toFixed(2)}\n`;
    });

    // Monthly trends
    csv += '\nMonthly Trends\n';
    csv += 'Month,Spending,Order Count\n';
    data.monthly_labels?.forEach((label, i) => {
      csv += `${label},$${data.monthly_spending[i]?.toFixed(2) || '0.00'},${data.monthly_order_counts[i] || 0}\n`;
    });

    // Product spending
    csv += '\nSpending by Product\n';
    csv += 'Product,Amount\n';
    data.products?.forEach((product, i) => {
      csv += `${product},$${data.product_spending[i]?.toFixed(2) || '0.00'}\n`;
    });

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `financial_report_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const exportToPDF = () => {
    if (!data) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Financial Report</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; color: #333; }
          h1 { color: #1e3a8a; border-bottom: 2px solid #1e3a8a; padding-bottom: 10px; }
          h2 { color: #1e3a8a; margin-top: 30px; }
          table { width: 100%; border-collapse: collapse; margin: 15px 0; }
          th, td { border: 1px solid #ddd; padding: 10px 12px; text-align: left; }
          th { background-color: #1e3a8a; color: white; }
          tr:nth-child(even) { background-color: #f8f9fa; }
          .summary-cards { display: flex; gap: 20px; margin: 20px 0; }
          .card { flex: 1; border: 1px solid #ddd; border-radius: 8px; padding: 15px; text-align: center; }
          .card h3 { margin: 0; color: #1e3a8a; }
          .card p { margin: 5px 0 0; color: #666; font-size: 14px; }
          .date { color: #666; font-size: 14px; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <h1>Financial Report</h1>
        <p class="date">Generated on ${new Date().toLocaleDateString()}</p>

        <div class="summary-cards">
          <div class="card"><h3>$${data.total_spent?.toFixed(2)}</h3><p>Total Spent</p></div>
          <div class="card"><h3>${data.total_orders}</h3><p>Total Orders</p></div>
          <div class="card"><h3>$${data.avg_order_value?.toFixed(2)}</h3><p>Avg Order Value</p></div>
          <div class="card"><h3>$${data.monthly_average?.toFixed(2)}</h3><p>Monthly Average</p></div>
        </div>

        <h2>Financial Summary</h2>
        <table>
          <thead><tr><th>Period</th><th>Orders</th><th>Total Spending</th><th>Avg Order Value</th></tr></thead>
          <tbody>
            ${data.summary?.map(row => `
              <tr><td>${row.period}</td><td>${row.orders}</td><td>$${row.spending.toFixed(2)}</td><td>$${row.avg_order.toFixed(2)}</td></tr>
            `).join('')}
          </tbody>
        </table>

        <h2>Monthly Trends</h2>
        <table>
          <thead><tr><th>Month</th><th>Spending</th><th>Orders</th></tr></thead>
          <tbody>
            ${data.monthly_labels?.map((label, i) => `
              <tr><td>${label}</td><td>$${data.monthly_spending[i]?.toFixed(2) || '0.00'}</td><td>${data.monthly_order_counts[i] || 0}</td></tr>
            `).join('')}
          </tbody>
        </table>

        <h2>Spending by Product</h2>
        <table>
          <thead><tr><th>Product</th><th>Amount</th></tr></thead>
          <tbody>
            ${data.products?.map((product, i) => `
              <tr><td>${product}</td><td>$${data.product_spending[i]?.toFixed(2) || '0.00'}</td></tr>
            `).join('')}
          </tbody>
        </table>

        <script>window.onload = function() { window.print(); }</script>
      </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
  };

  return (
    <div className="d-flex flex-column min-vh-100 app-background">
      <Header />
      <div className="flex-grow-1">
        <Container className="py-3 py-md-5 page-content-padding">
          <Row className="mb-3 mb-md-4 align-items-center">
            <Col xs={12} md={8} className="mb-3 mb-md-0">
              <h1 className="display-4 fw-bold text-primary mb-2 d-flex align-items-center gap-2">
                <ChartBarIcon className="page-header-icon" style={{ width: '40px', height: '40px' }} />
                Financial Reports
              </h1>
            </Col>
            <Col xs={12} md={4}>
              <Form.Select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="modern-input"
              >
                <option value="last-30-days">Last 30 Days</option>
                <option value="last-90-days">Last 90 Days</option>
                <option value="last-6-months">Last 6 Months</option>
                <option value="year-to-date">Year to Date</option>
                <option value="all-time">All Time</option>
                <option value="custom">Custom Range</option>
              </Form.Select>
              {dateRange === 'custom' && (
                <Row className="mt-2 g-2">
                  <Col xs={6}>
                    <Form.Control
                      type="date"
                      value={customStart}
                      onChange={(e) => setCustomStart(e.target.value)}
                      placeholder="Start date"
                    />
                  </Col>
                  <Col xs={6}>
                    <Form.Control
                      type="date"
                      value={customEnd}
                      min={customStart}
                      onChange={(e) => setCustomEnd(e.target.value)}
                      placeholder="End date"
                    />
                  </Col>
                </Row>
              )}
            </Col>
          </Row>

        {/* Summary Cards */}
        <Row className="mb-4 g-3">
          <Col xs={6} sm={3}>
            <Card className="text-center shadow-sm">
              <Card.Body className="p-2 p-sm-3">
                <h3 className="text-primary" style={{ fontSize: 'clamp(1rem, 3vw, 1.75rem)' }}>${data?.total_spent?.toFixed(2) || '0.00'}</h3>
                <p className="text-muted mb-0" style={{ fontSize: 'clamp(0.7rem, 2vw, 0.875rem)' }}>Total Spent</p>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={6} sm={3}>
            <Card className="text-center shadow-sm">
              <Card.Body className="p-2 p-sm-3">
                <h3 className="text-success" style={{ fontSize: 'clamp(1rem, 3vw, 1.75rem)' }}>{data?.total_orders || 0}</h3>
                <p className="text-muted mb-0" style={{ fontSize: 'clamp(0.7rem, 2vw, 0.875rem)' }}>Total Orders</p>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={6} sm={3}>
            <Card className="text-center shadow-sm">
              <Card.Body className="p-2 p-sm-3">
                <h3 className="text-info" style={{ fontSize: 'clamp(1rem, 3vw, 1.75rem)' }}>${data?.avg_order_value?.toFixed(2) || '0.00'}</h3>
                <p className="text-muted mb-0" style={{ fontSize: 'clamp(0.7rem, 2vw, 0.875rem)' }}>Avg Order Value</p>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={6} sm={3}>
            <Card className="text-center shadow-sm">
              <Card.Body className="p-2 p-sm-3">
                <h3 className="text-warning" style={{ fontSize: 'clamp(1rem, 3vw, 1.75rem)' }}>${data?.monthly_average?.toFixed(2) || '0.00'}</h3>
                <p className="text-muted mb-0" style={{ fontSize: 'clamp(0.7rem, 2vw, 0.875rem)' }}>Monthly Average</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Charts - Responsive Layout */}
        <Row className="mb-4 g-4">
          <Col xs={12} lg={8} className="mb-4 mb-lg-0">
            <Card className="shadow-sm">
              <Card.Header>
                <h5 className="mb-0">Spending Trends</h5>
              </Card.Header>
              <Card.Body>
                <div className="chart-responsive">
                  <Line data={monthlyData} options={{ responsive: true, maintainAspectRatio: false }} />
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12} lg={4}>
            <Card className="shadow-sm">
              <Card.Header>
                <h5 className="mb-0">Spending by Product</h5>
              </Card.Header>
              <Card.Body>
                <div className="chart-responsive">
                  <Bar data={productData} options={{ responsive: true, maintainAspectRatio: false }} />
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Summary Table */}
        <Row>
          <Col>
            <Card className="shadow-sm">
              <Card.Header>
                <h5 className="mb-0">Financial Summary</h5>
              </Card.Header>
              <Card.Body>
                <Table striped hover responsive>
                  <thead>
                    <tr>
                      <th>Period</th>
                      <th>Orders</th>
                      <th>Total Spending</th>
                      <th>Average Order Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.summary?.map((row) => (
                      <tr key={row.period}>
                        <td><strong>{row.period}</strong></td>
                        <td>{row.orders}</td>
                        <td>${row.spending.toFixed(2)}</td>
                        <td>${row.avg_order.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Export Options */}
        <Row className="mt-4">
          <Col xs={12}>
            <Card className="shadow-sm">
              <Card.Body>
                <h6 className="mb-3">Export Options</h6>
                <div className="d-flex flex-wrap gap-2 gap-sm-3">
                  <Button variant="outline-primary" onClick={exportToExcel} className="flex-grow-1 flex-sm-grow-0">
                    <DocumentArrowDownIcon style={{ width: '16px', height: '16px' }} className="me-2" />
                    Export to Excel
                  </Button>
                  <Button variant="outline-secondary" onClick={exportToPDF} className="flex-grow-1 flex-sm-grow-0">
                    <DocumentIcon style={{ width: '16px', height: '16px' }} className="me-2" />
                    Export to PDF
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        </Container>
      </div>
      <Footer />
    </div>
  );
};

export default FinancialReports;